import { type ApiClient, isApiError } from "@pokifolio/api-client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, type RouterHistory } from "@tanstack/react-router";
import { useState } from "react";

import { Toaster } from "../components/ui/sonner";
import { ApiProvider } from "./api-provider";
import { createAppRouter } from "./router";
import { ThemeProvider } from "./theme/theme-provider";

/** Retry only problems that may go away (network, server errors), never "not allowed" or "invalid". */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (isApiError(error) && error.status >= 400 && error.status < 500) return false;
  return failureCount < 2;
}

/** Wait 0.5 s, then 1 s, before retrying (capped at 5 s). */
const retryDelayMs = (attempt: number): number => Math.min(500 * 2 ** attempt, 5_000);

export function App({ api, history }: { api: ApiClient; history?: RouterHistory }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: shouldRetry, retryDelay: retryDelayMs, refetchOnWindowFocus: false },
          mutations: { retry: false },
        },
      }),
  );
  const [router] = useState(() => createAppRouter({ api, queryClient }, history));

  return (
    <ThemeProvider>
      <ApiProvider api={api}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <Toaster position="bottom-right" richColors closeButton />
        </QueryClientProvider>
      </ApiProvider>
    </ThemeProvider>
  );
}
